import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import './ReassignmentModal.css';

const ReassignmentModal = ({
  show,
  onClose,
  title = 'Reassign Patients',
  description,
  patients = [],
  alternatives = [],
  reassignments = {},
  onReassignmentChange,
  onAssignAll,
  onSave,
  isSaving = false,
  saveLabel = 'Save',
  optionPlaceholder = 'Select...',
  showOptionPlaceholder = true,
  showMeta = false,
  showIndex = false,
  hideCurrentColumn = false,
  currentLabel = 'Current',
  reassignToLabel = 'Reassign to',
  bulkAssignLabel = 'Assign all to',
  bulkApplyLabel = 'Apply to all',
  getPatientName,
  getCurrentAssignee,
  getAlternativeId,
  getAlternativeName
}) => {
  const [bulkAssignId, setBulkAssignId] = useState('');

  const patientName = getPatientName || ((p) => `${p?.first_name || ''} ${p?.last_name || ''}`.trim() || 'N/A');
  const currentAssignee = getCurrentAssignee || (() => 'N/A');
  const alternativeId = getAlternativeId || ((x) => x?.id);
  const alternativeName = getAlternativeName || ((x) => `${x?.first_name || ''} ${x?.last_name || ''}`.trim() || 'N/A');
  const getAssignment = (patientId) => {
    const raw = reassignments?.[patientId] ?? reassignments?.[String(patientId)] ?? '';
    return typeof raw === 'string' ? raw.trim() : raw;
  };
  const hasUnassigned = patients.some((p) => !getAssignment(p.id));
  const showBulkAssign = Boolean(onAssignAll) && patients.length > 0 && alternatives.length > 0;

  useEffect(() => {
    if (!show) {
      setBulkAssignId('');
    }
  }, [show]);

  const handleApplyBulk = () => {
    if (!bulkAssignId || !onAssignAll) return;
    onAssignAll(bulkAssignId);
  };

  if (!show || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="reassign-modal-overlay" role="presentation">
      <div
        className="reassign-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reassign-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="reassign-modal-header">
          <h2 id="reassign-modal-title" className="reassign-modal-title">{title}</h2>
          <button type="button" className="reassign-modal-close" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="reassign-modal-body">
          {description ? <p className="reassign-modal-description">{description}</p> : null}

          {showMeta ? (
            <div className="reassign-modal-meta">
              <span>{patients.length} patient(s)</span>
              <span>{alternatives.length} option(s)</span>
            </div>
          ) : null}

          {showBulkAssign ? (
            <div className="reassign-modal-bulk">
              <div className="reassign-modal-bulk-fields">
                <label className="reassign-modal-bulk-label" htmlFor="reassign-modal-bulk-select">
                  {bulkAssignLabel}
                </label>
                <select
                  id="reassign-modal-bulk-select"
                  className="reassign-modal-select reassign-modal-bulk-select"
                  value={bulkAssignId}
                  onChange={(e) => setBulkAssignId(e.target.value)}
                >
                  <option value="">{optionPlaceholder}</option>
                  {alternatives.map((item) => (
                    <option key={alternativeId(item)} value={alternativeId(item)}>
                      {alternativeName(item)}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="reassign-modal-btn reassign-modal-btn--bulk"
                onClick={handleApplyBulk}
                disabled={!bulkAssignId}
              >
                {bulkApplyLabel}
              </button>
            </div>
          ) : null}

          <div className="reassign-modal-table-wrap">
            <table className="reassign-modal-table">
              <thead>
                <tr>
                  {showIndex ? <th>#</th> : null}
                  <th>Patient</th>
                  {!hideCurrentColumn ? <th>{currentLabel}</th> : null}
                  <th>{reassignToLabel}</th>
                </tr>
              </thead>
              <tbody>
                {patients.map((patient, idx) => (
                  <tr key={patient.id}>
                    {showIndex ? <td className="reassign-modal-index">{idx + 1}</td> : null}
                    <td className="reassign-modal-patient">{patientName(patient)}</td>
                    {!hideCurrentColumn ? (
                      <td className="reassign-modal-current">{currentAssignee(patient)}</td>
                    ) : null}
                    <td>
                      <select
                        className="reassign-modal-select"
                        value={getAssignment(patient.id) || ''}
                        onChange={(e) => onReassignmentChange(patient.id, e.target.value)}
                      >
                        {showOptionPlaceholder ? <option value="">{optionPlaceholder}</option> : null}
                        {alternatives.map((item) => (
                          <option key={alternativeId(item)} value={alternativeId(item)}>
                            {alternativeName(item)}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="reassign-modal-footer">
          <button type="button" className="reassign-modal-btn reassign-modal-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="reassign-modal-btn reassign-modal-btn--primary"
            onClick={onSave}
            disabled={isSaving || hasUnassigned}
          >
            {isSaving ? <FontAwesomeIcon icon={faSpinner} spin className="reassign-modal-spinner" /> : null}
            {saveLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReassignmentModal;
